// === Advisory Filtering ===

import store from '../state/store.js';

// Default anchor months per herd type. These must mirror SEASON_DEFAULTS
// in profile-service.js so a user who accepts the default profile sees
// zero shift (cards land on the month the data file lists).
const DEFAULT_ANCHORS_BY_HERD = {
  dairy: { calving: 2, turnout: 4, housing: 10, breeding: 5, dryingOff: 11 },
  beef:  { calving: 2, turnout: 4, housing: 11, breeding: 5, dryingOff: 11 }
};

function normalizeHerd(input) {
  const val = (input || '').toLowerCase();
  if (val === 'store' || val === 'fattening') return 'beef';
  return val;
}

function getDefaultAnchors() {
  const herd = normalizeHerd(store.farmProfile.herdType);
  return DEFAULT_ANCHORS_BY_HERD[herd] || DEFAULT_ANCHORS_BY_HERD.dairy;
}

function matchesHerd(item) {
  if (!store.farmProfile.herdType) return true;
  const chosen = normalizeHerd(store.farmProfile.herdType);
  const relevant = (item.meta && item.meta.relevantHerdTypes)
    ? item.meta.relevantHerdTypes.map(h => h.toLowerCase())
    : [];
  return relevant.includes(chosen);
}

// Calving months (1-indexed) that count as each calving season.
const CALVING_SEASON_MONTHS = {
  Spring: [1, 2, 3, 4],
  Autumn: [8, 9, 10, 11],
};

// Cards flagged with meta.calvingSeason only show when the farmer's calving
// month falls in that season — e.g. an autumn-calving card is hidden for a
// spring-calving herd. No flag → always eligible.
function matchesCalvingSeason(item) {
  const season = item.meta && item.meta.calvingSeason;
  if (!season) return true;
  const calving = store.farmSeasons?.calving;
  if (!calving) return true;
  return (CALVING_SEASON_MONTHS[season] || []).includes(calving);
}

function shiftMonth(month, shift) {
  return ((month - 1 + shift + 120) % 12) + 1;
}

export function getShiftedMonths(item) {
  if (item.meta.yearRound) return null;

  const baseMonths = item.meta.months || [];
  const anchor = item.meta.anchor;

  if (!anchor) return baseMonths;

  const farmerMonth = store.farmSeasons?.[anchor];
  const defaultMonth = getDefaultAnchors()[anchor];
  if (!farmerMonth || !defaultMonth) return baseMonths;

  const shift = farmerMonth - defaultMonth;
  if (shift === 0) return baseMonths;

  return baseMonths.map(m => shiftMonth(m, shift));
}

// Months a task effectively appears in, honouring a user month-override.
// Returns null for year-round (no override), or an array of 1-indexed months.
export function getEffectiveMonths(item) {
  const override = store.monthOverrides?.[item.id];
  if (override !== undefined && override !== null) {
    return [override + 1]; // stored 0-indexed → 1-indexed
  }
  return getShiftedMonths(item);
}

export function filterAdvisories() {
  const targetMonth = store.currentMonth + 1;
  const iBeef = normalizeHerd(store.farmProfile.herdType) === 'beef';

  return store.seed.filter(item => {
    // Hide dryingOff cards for Beef farmers
    if (iBeef && item.meta.anchor === 'dryingOff') return false;

    if (!matchesHerd(item)) return false;
    if (!matchesCalvingSeason(item)) return false;

    const itemMonths = getEffectiveMonths(item);
    return (itemMonths === null) || itemMonths.includes(targetMonth);
  });
}
